import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import REPORTS, { getReportCategories, getReportsByCategory, getReportById } from '../reports/reportRegistry';
import { executeReport, executeWorkbook } from '../reports/reportEngine';
import { fetchIntacctData } from '../utils/api';
import InteractiveReportViewer from './InteractiveReportViewer';

const ReportsModePanel = ({ config, loading, setLoading, setStatus, isTestMode }) => {
  const [selectedReportId, setSelectedReportId] = useState('');
  const [reportCategory, setReportCategory] = useState('All');
  const [paramValues, setParamValues] = useState({});
  const [enabledOptionalIds, setEnabledOptionalIds] = useState([]);
  const [lastResult, setLastResult] = useState(null);
  const [showColumns, setShowColumns] = useState(false);
  const [showColumnOrder, setShowColumnOrder] = useState(false);
  const [showSortOrder, setShowSortOrder] = useState(false);
  const [columnOrder, setColumnOrder] = useState(null);
  const [sortRules, setSortRules] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);

  // Project search state (for workbooks)
  const [projectList, setProjectList] = useState([]);     // cached full list
  const [projectSearch, setProjectSearch] = useState('');  // search text
  const [projectDropOpen, setProjectDropOpen] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const projectSearchRef = useRef(null);

  // Interactive viewer state — multiple open reports
  const [openReports, setOpenReports] = useState([]); // array of report tabs
  const [activeTabIdx, setActiveTabIdx] = useState(-1); // -1 = setup, 0+ = report tab

  const categories = useMemo(() => getReportCategories(), []);
  const filteredReports = useMemo(() => getReportsByCategory(reportCategory), [reportCategory]);
  const selectedReport = useMemo(() => getReportById(selectedReportId), [selectedReportId]);

  const columnGroups = useMemo(() => {
    if (!selectedReport?.optionalColumns) return {};
    const groups = {};
    for (const col of selectedReport.optionalColumns) {
      const group = col.group || 'Other';
      if (!groups[group]) groups[group] = [];
      groups[group].push(col);
    }
    return groups;
  }, [selectedReport]);

  const triggeredQueries = useMemo(() => {
    if (!selectedReport?.additionalQueries) return [];
    return selectedReport.additionalQueries.filter(aq => {
      const cols = (selectedReport.optionalColumns || []).filter(c => c.queryId === aq.id);
      return cols.some(c => enabledOptionalIds.includes(c.id));
    });
  }, [selectedReport, enabledOptionalIds]);

  // Filtered project list based on search text
  const filteredProjects = useMemo(() => {
    if (!projectSearch.trim()) return projectList.slice(0, 50); // show first 50 when empty
    const term = projectSearch.toLowerCase();
    return projectList.filter(p =>
      p.PROJECTID.toLowerCase().includes(term) ||
      (p.NAME || '').toLowerCase().includes(term)
    ).slice(0, 50);
  }, [projectList, projectSearch]);

  // Close project dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (projectSearchRef.current && !projectSearchRef.current.contains(e.target)) {
        setProjectDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ============ FETCH PROJECTS (for workbook project search) ============
  const fetchProjects = useCallback(async () => {
    if (projectList.length > 0) return; // already cached
    if (!config?.companyId || !config?.userId) return;

    setProjectsLoading(true);
    try {
      const result = await fetchIntacctData(
        config, 'PROJECT', 'PROJECTID,NAME', "STATUS = 'active'", 2000, 0, true
      );
      const projects = (result.data || []).sort((a, b) =>
        (a.PROJECTID || '').localeCompare(b.PROJECTID || '')
      );
      setProjectList(projects);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    } finally {
      setProjectsLoading(false);
    }
  }, [config, projectList.length]);

  const handleReportChange = (reportId) => {
    setSelectedReportId(reportId);
    setLastResult(null);
    setEnabledOptionalIds([]);
    setShowColumns(false);
    setShowColumnOrder(false);
    setShowSortOrder(false);
    setColumnOrder(null);
    setSortRules([]);
    setProjectSearch('');
    const report = getReportById(reportId);
    if (report) {
      const defaults = {};
      report.parameters.forEach(p => {
        defaults[p.id] = typeof p.default === 'function' ? p.default() : p.default;
      });
      setParamValues(defaults);

      // If this is a workbook, pre-fetch the project list
      if (report.isWorkbook) {
        fetchProjects();
      }
    } else {
      setParamValues({});
    }
  };

  const handleParamChange = (paramId, value) => {
    setParamValues(prev => ({ ...prev, [paramId]: value }));
  };

  // ============ PROJECT SELECTION (for workbooks) ============
  const handleProjectSelect = (project) => {
    const displayText = project.PROJECTID + '\u2014' + (project.NAME || '');
    setProjectSearch(displayText);
    setParamValues(prev => ({ ...prev, projectId: project.PROJECTID }));
    setProjectDropOpen(false);
  };

  const toggleOptionalColumn = (colId) => {
    setEnabledOptionalIds(prev =>
      prev.includes(colId) ? prev.filter(id => id !== colId) : [...prev, colId]
    );
    setColumnOrder(null);
  };

  const toggleGroup = (groupCols) => {
    const groupIds = groupCols.map(c => c.id);
    const allEnabled = groupIds.every(id => enabledOptionalIds.includes(id));
    if (allEnabled) {
      setEnabledOptionalIds(prev => prev.filter(id => !groupIds.includes(id)));
    } else {
      setEnabledOptionalIds(prev => [...new Set([...prev, ...groupIds])]);
    }
    setColumnOrder(null);
  };

  // ============ COLUMN ORDER ============
  const moveColumn = (fromIdx, toIdx) => {
    if (!columnOrder || fromIdx === toIdx) return;
    const newOrder = [...columnOrder];
    const [moved] = newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, moved);
    setColumnOrder(newOrder);
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e) => { e.preventDefault(); };
  const handleDrop = (e, idx) => {
    e.preventDefault();
    if (dragIdx !== null) moveColumn(dragIdx, idx);
    setDragIdx(null);
  };

  // ============ SORT ORDER ============
  const addSortRule = () => {
    if (!lastResult?.columns?.length) return;
    const usedCols = sortRules.map(r => r.column);
    const available = (columnOrder || lastResult.columns).filter(c => !usedCols.includes(c));
    if (available.length === 0) return;
    setSortRules(prev => [...prev, { column: available[0], direction: 'asc' }]);
  };

  const updateSortRule = (idx, field, value) => {
    setSortRules(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const removeSortRule = (idx) => {
    setSortRules(prev => prev.filter((_, i) => i !== idx));
  };

  // ============ REPORT / WORKBOOK EXECUTION ============
  const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';

  const handleRunReport = async () => {
    if (!selectedReport) return;

    // Validation for workbooks: project is required
    if (selectedReport.isWorkbook && !paramValues.projectId) {
      setStatus('Please select a project first.');
      return;
    }

    setLoading(true);
    setStatus('');
    setLastResult(null);

    try {
      if (isTestMode) {
        await new Promise(r => setTimeout(r, 800));
        setStatus('Success! (Test Mode - no data fetched)');
        setLoading(false);
        return;
      }

      if (selectedReport.isWorkbook) {
        // ---- WORKBOOK EXECUTION ----
        const sheetResults = await executeWorkbook(config, selectedReport, paramValues, (msg) => {
          setStatus(msg);
        });

        const totalRows = sheetResults.reduce((sum, s) => sum + (s.data?.length || 0), 0);
        setStatus('Generating ' + sheetResults.length + '-sheet workbook (' + totalRows + ' total rows)...');

        await writeWorkbookToExcel(sheetResults, selectedReport);

        // Build combined summary
        const summaryLines = sheetResults.map(s => {
          const rowCount = (s.data || []).length;
          return s.sheetName + ': ' + rowCount + ' rows';
        });
        setLastResult({ summary: { lines: ['Workbook generated successfully!', ...summaryLines] } });
        setStatus('Downloaded! ' + selectedReport.name + ': ' + totalRows + ' total rows');
      } else {
        // ---- SINGLE REPORT EXECUTION ----
        // Pre-fetch ALL optional columns so users can add them instantly later
        const allOptIds = (selectedReport.optionalColumns || []).map(c => c.id);
        const result = await executeReport(config, selectedReport, paramValues, allOptIds, (msg) => {
          setStatus(msg);
        });

        if (sortRules.length > 0 && !result.detailRowIndices) {
          result.data.sort((a, b) => {
            for (const rule of sortRules) {
              const aVal = a[rule.column];
              const bVal = b[rule.column];
              let cmp = 0;
              if (typeof aVal === 'number' && typeof bVal === 'number') {
                cmp = aVal - bVal;
              } else {
                cmp = String(aVal || '').localeCompare(String(bVal || ''));
              }
              if (cmp !== 0) return rule.direction === 'desc' ? -cmp : cmp;
            }
            return 0;
          });
        }

        if (columnOrder) {
          const resultColSet = new Set(result.columns);
          const orderColSet = new Set(columnOrder);
          const sameColumns = resultColSet.size === orderColSet.size &&
            [...resultColSet].every(c => orderColSet.has(c));
          if (sameColumns) {
            result.columns = columnOrder;
          } else {
            setColumnOrder([...result.columns]);
          }
        }

        if (!columnOrder) {
          setColumnOrder([...result.columns]);
        }

        // Figure out which columns to hide initially
        // (optional columns the user didn't explicitly select)
        const userSelectedSet = new Set(enabledOptionalIds);
        const defaultHiddenCols = (selectedReport.optionalColumns || [])
          .filter(c => !userSelectedSet.has(c.id))
          .map(c => c.label || c.id);

        // Create a new report tab
        const newReport = {
          id: Date.now(),
          name: selectedReport.name,
          reportDef: selectedReport,
          viewerData: {
            columns: result.columns,
            data: result.data,
            summary: result.summary,
            formatRules: result.formatRules || selectedReport.formatRules || {},
            detailRowIndices: result.detailRowIndices || null,
            _fullResult: result,
          },
          lastRunTime: new Date(),
          defaultHiddenCols,
          paramValues: { ...paramValues },
          enabledOptionalIds: [...enabledOptionalIds],
        };

        setOpenReports(prev => [...prev, newReport]);
        setActiveTabIdx(openReports.length); // switch to new tab
        setLastResult(result);
        const summaryText = result.summary?.lines?.slice(0, 2).join(' | ') || (result.data.length + ' rows');
        setStatus('Report ready \u2014 ' + summaryText);
      }
    } catch (error) {
      setStatus('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // ============ EXCEL WRITERS ============
  const writeReportToExcel = async (result, reportDef) => {
    const { columns, data, formatRules, detailRowIndices, groupedColumns, rowOutlineLevels } = result;
    if (!data || data.length === 0) throw new Error('Report returned no data.');

    const paramParts = reportDef.parameters
      .filter(p => paramValues[p.id] !== '' && paramValues[p.id] !== undefined && paramValues[p.id] !== false)
      .map(p => p.label + ': ' + paramValues[p.id]);
    if (enabledOptionalIds.length > 0) paramParts.push('+' + enabledOptionalIds.length + ' optional column(s)');

    let response;
    try {
      response = await fetch(API_BASE + '/api/generate-report-xlsx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reportName: reportDef.name,
          paramSummary: paramParts.join('  |  '),
          columns,
          data,
          formatRules: formatRules || {},
          detailRowIndices: detailRowIndices || [],
          groupedColumns: groupedColumns || [],
          rowOutlineLevels: rowOutlineLevels || [],
        }),
      });
    } catch (fetchErr) {
      throw new Error('Connection to report server failed. (' + fetchErr.message + ')');
    }

    if (!response.ok) {
      const errText = await response.text();
      throw new Error('Report generation failed (' + response.status + '): ' + errText);
    }

    const blob = await response.blob();
    downloadBlob(blob, reportDef.name);
  };

  const writeWorkbookToExcel = async (sheetResults, workbookDef) => {
    const projectId = paramValues.projectId || 'Unknown';
    const workbookName = 'Project ' + projectId + ' Workbook';

    const paramParts = [];
    paramParts.push('Project: ' + projectId);
    if (paramValues.includeClosed) paramParts.push('Include Closed: Yes');
    if (paramValues.includeStandaloneAR) paramParts.push('Standalone AR: Yes');
    if (paramValues.enableTimesheetTying) paramParts.push('Timesheet Tying: Yes');

    const sheets = sheetResults.map(s => ({
      sheetName: s.sheetName,
      columns: s.columns,
      data: s.data,
      formatRules: s.formatRules || {},
      detailRowIndices: s.detailRowIndices || [],
      groupedColumns: s.groupedColumns || [],
      rowOutlineLevels: s.rowOutlineLevels || [],
      detailLevel: s.detailLevel,
    }));

    let response;
    try {
      response = await fetch(API_BASE + '/api/generate-workbook-xlsx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workbookName: workbookName,
          paramSummary: paramParts.join('  |  '),
          sheets: sheets,
        }),
      });
    } catch (fetchErr) {
      throw new Error('Connection to report server failed. (' + fetchErr.message + ')');
    }

    if (!response.ok) {
      const errText = await response.text();
      throw new Error('Workbook generation failed (' + response.status + '): ' + errText);
    }

    const blob = await response.blob();
    downloadBlob(blob, workbookName);
  };

  const downloadBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const safeName = name.replace(/[^a-zA-Z0-9 _-]/g, '');
    const timestamp = new Date().toISOString().slice(0, 10);
    const filename = safeName + ' ' + timestamp + '.xlsx';

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ============ MULTI-TAB REPORT HANDLERS ============
  const handleDownloadExcel = async (tabIdx) => {
    const report = openReports[tabIdx];
    if (!report?.viewerData?._fullResult || !report.reportDef) return;
    setLoading(true);
    setStatus('Generating Excel...');
    try {
      await writeReportToExcel(report.viewerData._fullResult, report.reportDef);
      setStatus('Excel downloaded!');
    } catch (err) {
      setStatus('Download error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshReport = async (tabIdx) => {
    const report = openReports[tabIdx];
    if (!report) return;
    setLoading(true);
    setStatus('Refreshing ' + report.name + '...');
    try {
      const allOptIds = (report.reportDef.optionalColumns || []).map(c => c.id);
      const result = await executeReport(config, report.reportDef, report.paramValues, allOptIds, (msg) => {
        setStatus(msg);
      });

      const userSelectedSet = new Set(report.enabledOptionalIds);
      const defaultHiddenCols = (report.reportDef.optionalColumns || [])
        .filter(c => !userSelectedSet.has(c.id))
        .map(c => c.label || c.id);

      setOpenReports(prev => prev.map((r, i) => i === tabIdx ? {
        ...r,
        viewerData: {
          columns: result.columns,
          data: result.data,
          summary: result.summary,
          formatRules: result.formatRules || report.reportDef.formatRules || {},
          detailRowIndices: result.detailRowIndices || null,
          _fullResult: result,
        },
        lastRunTime: new Date(),
        defaultHiddenCols,
      } : r));
      const summaryText = result.summary?.lines?.slice(0, 2).join(' | ') || (result.data.length + ' rows');
      setStatus('Refreshed \u2014 ' + summaryText);
    } catch (err) {
      setStatus('Refresh error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const closeReport = (tabIdx) => {
    setOpenReports(prev => prev.filter((_, i) => i !== tabIdx));
    // Adjust activeTabIdx
    if (activeTabIdx === tabIdx) {
      setActiveTabIdx(-1); // go back to setup
    } else if (activeTabIdx > tabIdx) {
      setActiveTabIdx(prev => prev - 1);
    }
  };

  // ============ PARAMETER RENDERING ============
  const renderParamInput = (param) => {
    const value = paramValues[param.id] ?? '';

    // Special rendering for project search
    if (param.type === 'project-search') {
      return (
        <div ref={projectSearchRef} className="relative">
          <input
            type="text"
            value={projectSearch}
            onChange={(e) => {
              setProjectSearch(e.target.value);
              setProjectDropOpen(true);
              // If user clears or modifies the text, clear the selected projectId
              // unless the text still matches a valid project
              const match = projectList.find(p => {
                const display = p.PROJECTID + '\u2014' + (p.NAME || '');
                return display === e.target.value;
              });
              if (!match) {
                setParamValues(prev => ({ ...prev, projectId: '' }));
              }
            }}
            onFocus={() => {
              setProjectDropOpen(true);
              fetchProjects();
            }}
            placeholder={projectsLoading ? 'Loading projects...' : 'Search by ID or name...'}
            className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-sm text-white placeholder-gray-500"
          />
          {/* Selected indicator */}
          {paramValues.projectId && (
            <span className="absolute right-2 top-2.5 text-green-400 text-xs">\u2713</span>
          )}
          {/* Dropdown */}
          {projectDropOpen && !projectsLoading && (
            <div className="absolute z-50 w-full mt-1 max-h-48 overflow-y-auto bg-gray-700 border border-gray-600 rounded shadow-lg">
              {filteredProjects.length === 0 ? (
                <div className="px-3 py-2 text-xs text-gray-500">
                  {projectList.length === 0 ? 'No projects loaded' : 'No matches found'}
                </div>
              ) : (
                filteredProjects.map(p => (
                  <button
                    key={p.PROJECTID}
                    onClick={() => handleProjectSelect(p)}
                    className="w-full text-left px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-600 hover:text-white truncate"
                  >
                    <span className="font-medium text-yellow-400">{p.PROJECTID}</span>
                    <span className="text-gray-500"> \u2014 </span>
                    <span>{p.NAME || ''}</span>
                  </button>
                ))
              )}
              {filteredProjects.length >= 50 && (
                <div className="px-3 py-1 text-[10px] text-gray-500 border-t border-gray-600">
                  Showing first 50 results. Type to narrow down.
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    switch (param.type) {
      case 'date':
        return <input type="date" value={value} onChange={(e) => handleParamChange(param.id, e.target.value)} className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-sm text-white" />;
      case 'number':
        return <input type="number" value={value} min={param.min ?? 1} max={param.max ?? 999}
          onChange={(e) => handleParamChange(param.id, parseInt(e.target.value) || 0)}
          className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-sm text-white" />;
      case 'select':
        return (
          <select value={value} onChange={(e) => handleParamChange(param.id, e.target.value)} className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-sm text-white">
            {(param.options || []).map(opt => {
              const optVal = typeof opt === 'object' ? opt.value : opt;
              const optLabel = typeof opt === 'object' ? opt.label : opt;
              return <option key={optVal} value={optVal}>{optLabel}</option>;
            })}
          </select>
        );
      case 'checkbox':
        return (
          <label className="flex items-center space-x-2 cursor-pointer">
            <input type="checkbox" checked={!!value} onChange={(e) => handleParamChange(param.id, e.target.checked)} className="rounded text-yellow-400 bg-gray-700 border-gray-600" />
            <span className="text-xs text-gray-400">{param.description || ''}</span>
          </label>
        );
      default:
        return <input type="text" value={value} onChange={(e) => handleParamChange(param.id, e.target.value)} placeholder={param.description || ''} className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-sm text-white placeholder-gray-500" />;
    }
  };

  const sortableColumns = columnOrder || lastResult?.columns || [];

  // ============ RENDER ============
  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* ── Report Tab Bar ── */}
      {openReports.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 8px',
          backgroundColor: '#1a1a2e', borderBottom: '1px solid #2a2a40',
          overflowX: 'auto', flexShrink: 0,
        }}>
          {/* Setup tab */}
          <button
            onClick={() => setActiveTabIdx(-1)}
            style={{
              padding: '5px 12px', fontSize: '11px', fontWeight: 600, borderRadius: '4px',
              border: activeTabIdx === -1 ? '1px solid #fbbf24' : '1px solid #3b3b5c',
              backgroundColor: activeTabIdx === -1 ? '#fbbf2420' : '#1e1e32',
              color: activeTabIdx === -1 ? '#fbbf24' : '#9ca3af',
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            + New Report
          </button>

          {/* Open report tabs */}
          {openReports.map((report, idx) => (
            <div key={report.id} style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '5px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '4px',
              border: activeTabIdx === idx ? '1px solid #fbbf24' : '1px solid #3b3b5c',
              backgroundColor: activeTabIdx === idx ? '#fbbf2420' : '#1e1e32',
              color: activeTabIdx === idx ? '#fbbf24' : '#9ca3af',
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
              onClick={() => setActiveTabIdx(idx)}
            >
              <span>{report.name}</span>
              <span
                onClick={(e) => { e.stopPropagation(); closeReport(idx); }}
                style={{ fontSize: '12px', color: '#6b7280', cursor: 'pointer', lineHeight: 1 }}
                title="Close report"
              >{'\u2715'}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Setup Panel (visible when no report tab is active) ── */}
      <div style={{ display: activeTabIdx === -1 ? 'block' : 'none', overflow: 'auto', flex: 1, padding: '16px 0' }}>
        <div className="max-w-4xl mx-auto w-full px-4">
        <div className="bg-gray-800 p-4 rounded shadow-sm border border-gray-700 mb-6">
      {/* Report Selection */}
      <div className="mb-3">
        <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Category</label>
        <select value={reportCategory} onChange={(e) => { setReportCategory(e.target.value); setSelectedReportId(''); setLastResult(null); }} className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-sm text-white">
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="mb-4">
        <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Report</label>
        <select value={selectedReportId} onChange={(e) => handleReportChange(e.target.value)} className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-sm text-white">
          <option value="">Select a report...</option>
          {filteredReports.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </div>

      {selectedReport && (
        <div className="mb-4 p-3 bg-gray-700/50 rounded border border-gray-600">
          <div className="text-xs text-gray-300 leading-relaxed">{selectedReport.description}</div>
          {selectedReport.isWorkbook ? (
            <div className="mt-2 text-[10px] text-gray-500">
              Sheets: {selectedReport.sheets.map(s => s.name).join(', ')}
              {' \u2014 '}Consolidated queries: {selectedReport.queries.length}
            </div>
          ) : (
            <div className="mt-2 text-[10px] text-gray-500">
              Queries: {selectedReport.queries.map(q => q.object).join(', ')}
            </div>
          )}
        </div>
      )}

      {/* Parameters */}
      {selectedReport?.parameters?.length > 0 && (
        <div className="mb-4">
          <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Parameters</label>
          <div className="space-y-3">
            {selectedReport.parameters.map(param => (
              <div key={param.id}>
                {param.type !== 'checkbox' && <label className="block text-xs text-gray-300 mb-1 font-medium">{param.label}</label>}
                {renderParamInput(param)}
                {param.type !== 'checkbox' && param.type !== 'project-search' && param.description && <div className="text-[10px] text-gray-500 mt-0.5">{param.description}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Customize Columns (not shown for workbooks) */}
      {!selectedReport?.isWorkbook && selectedReport?.optionalColumns?.length > 0 && (
        <div className="mb-4">
          <button onClick={() => setShowColumns(!showColumns)}
            className="w-full flex items-center justify-between p-2 bg-gray-700/50 rounded border border-gray-600 hover:bg-gray-700 transition-colors">
            <span className="text-xs font-bold text-gray-400 uppercase">
              Customize Columns
              {enabledOptionalIds.length > 0 && <span className="ml-2 text-yellow-400 font-normal">({enabledOptionalIds.length} selected)</span>}
            </span>
            <span className="text-gray-500 text-xs">{showColumns ? '\u25B2' : '\u25BC'}</span>
          </button>

          {showColumns && (
            <div className="mt-2 p-3 bg-gray-700/30 rounded border border-gray-600 space-y-3 max-h-64 overflow-y-auto">
              {Object.entries(columnGroups).map(([groupName, groupCols]) => {
                const groupIds = groupCols.map(c => c.id);
                const enabledCount = groupIds.filter(id => enabledOptionalIds.includes(id)).length;
                const allEnabled = enabledCount === groupIds.length;
                return (
                  <div key={groupName}>
                    <div className="flex items-center justify-between mb-1.5">
                      <button onClick={() => toggleGroup(groupCols)} className="text-[10px] font-bold text-gray-400 uppercase hover:text-gray-300">
                        {groupName} {enabledCount > 0 && <span className="ml-1 text-yellow-400">({enabledCount})</span>}
                      </button>
                      <button onClick={() => toggleGroup(groupCols)} className="text-[10px] text-gray-500 hover:text-gray-400">
                        {allEnabled ? 'clear all' : 'select all'}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                      {groupCols.map(col => (
                        <label key={col.id} className="flex items-center space-x-1.5 cursor-pointer group">
                          <input type="checkbox" checked={enabledOptionalIds.includes(col.id)} onChange={() => toggleOptionalColumn(col.id)}
                            className="rounded text-yellow-400 bg-gray-700 border-gray-600 w-3 h-3" />
                          <span className="text-[11px] text-gray-400 group-hover:text-gray-300 truncate" title={col.id}>{col.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Column Order — available after first run, not for workbooks */}
      {!selectedReport?.isWorkbook && columnOrder && columnOrder.length > 0 && (
        <div className="mb-4">
          <button onClick={() => setShowColumnOrder(!showColumnOrder)}
            className="w-full flex items-center justify-between p-2 bg-gray-700/50 rounded border border-gray-600 hover:bg-gray-700 transition-colors">
            <span className="text-xs font-bold text-gray-400 uppercase">
              Column Order <span className="ml-2 text-yellow-400 font-normal">(customizable)</span>
            </span>
            <span className="text-gray-500 text-xs">{showColumnOrder ? '\u25B2' : '\u25BC'}</span>
          </button>

          {showColumnOrder && (
            <div className="mt-2 p-2 bg-gray-700/30 rounded border border-gray-600 max-h-72 overflow-y-auto">
              <div className="text-[10px] text-gray-500 mb-2">Drag to reorder. Changes apply on next run.</div>
              {columnOrder.map((col, idx) => (
                <div key={col} draggable
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, idx)}
                  className={'flex items-center gap-2 px-2 py-1 rounded text-[11px] cursor-grab active:cursor-grabbing transition-colors ' +
                    (dragIdx === idx ? 'bg-yellow-400/20 text-yellow-300' : 'text-gray-400 hover:bg-gray-600/50')
                  }>
                  <span className="text-gray-600 select-none">{'\u2807'}</span>
                  <span className="text-gray-500 w-5 text-right select-none">{idx + 1}</span>
                  <span className="truncate">{col}</span>
                  <span className="ml-auto flex gap-0.5 opacity-0 hover:opacity-100" style={{ transition: 'opacity 0.15s' }}>
                    {idx > 0 && <button onClick={() => moveColumn(idx, idx - 1)} className="text-gray-500 hover:text-gray-300 px-1" title="Move up">{'\u2191'}</button>}
                    {idx < columnOrder.length - 1 && <button onClick={() => moveColumn(idx, idx + 1)} className="text-gray-500 hover:text-gray-300 px-1" title="Move down">{'\u2193'}</button>}
                  </span>
                </div>
              ))}
              <button onClick={() => setColumnOrder(lastResult?.columns ? [...lastResult.columns] : null)}
                className="mt-2 text-[10px] text-gray-500 hover:text-gray-400 underline">
                Reset to default order
              </button>
            </div>
          )}
        </div>
      )}

      {/* Sort Order (not for workbooks) */}
      {!selectedReport?.isWorkbook && sortableColumns.length > 0 && (
        <div className="mb-4">
          <button onClick={() => setShowSortOrder(!showSortOrder)}
            className="w-full flex items-center justify-between p-2 bg-gray-700/50 rounded border border-gray-600 hover:bg-gray-700 transition-colors">
            <span className="text-xs font-bold text-gray-400 uppercase">
              Sort Order
              {sortRules.length > 0 && <span className="ml-2 text-yellow-400 font-normal">({sortRules.length} rule{sortRules.length > 1 ? 's' : ''})</span>}
            </span>
            <span className="text-gray-500 text-xs">{showSortOrder ? '\u25B2' : '\u25BC'}</span>
          </button>

          {showSortOrder && (
            <div className="mt-2 p-3 bg-gray-700/30 rounded border border-gray-600">
              {sortRules.length === 0 && <div className="text-[10px] text-gray-500 mb-2">Using report default sort. Add rules to customize.</div>}
              {sortRules.map((rule, idx) => (
                <div key={idx} className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] text-gray-500 w-12">{idx === 0 ? 'Sort by' : 'Then by'}</span>
                  <select value={rule.column} onChange={(e) => updateSortRule(idx, 'column', e.target.value)}
                    className="flex-1 p-1.5 bg-gray-700 border border-gray-600 rounded text-[11px] text-white">
                    {sortableColumns.map(col => <option key={col} value={col}>{col}</option>)}
                  </select>
                  <select value={rule.direction} onChange={(e) => updateSortRule(idx, 'direction', e.target.value)}
                    className="w-20 p-1.5 bg-gray-700 border border-gray-600 rounded text-[11px] text-white">
                    <option value="asc">A {'\u2192'} Z</option>
                    <option value="desc">Z {'\u2192'} A</option>
                  </select>
                  <button onClick={() => removeSortRule(idx)} className="text-red-400 hover:text-red-300 text-sm px-1" title="Remove">{'\u2715'}</button>
                </div>
              ))}
              {sortRules.length < 5 && (
                <button onClick={addSortRule} className="text-[10px] text-yellow-400 hover:text-yellow-300 font-medium">+ Add sort rule</button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Run Button */}
      {selectedReport && (
        <button onClick={handleRunReport} disabled={loading || (selectedReport.isWorkbook && !paramValues.projectId)}
          className={'w-full py-3 px-4 rounded-md shadow-sm text-sm font-bold ' +
            (loading ? 'bg-gray-600 cursor-wait text-gray-400'
              : (selectedReport.isWorkbook && !paramValues.projectId) ? 'bg-gray-600 text-gray-500 cursor-not-allowed'
              : 'bg-yellow-400 hover:bg-yellow-300 text-gray-900')
          }>
          {loading ? (selectedReport.isWorkbook ? 'Generating Workbook...' : 'Running Report...')
            : selectedReport.isWorkbook
              ? (paramValues.projectId ? '\u26A1 Generate ' + selectedReport.name : 'Select a project to continue')
              : '\u26A1 Run ' + selectedReport.name}
        </button>
      )}

      {/* Results Summary (workbooks only — single reports go to viewer tabs) */}
      {lastResult?.summary && selectedReport?.isWorkbook && (
        <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded">
          <div className="text-xs font-bold text-green-400 mb-1">Workbook Complete</div>
          {lastResult.summary.lines?.map((line, idx) => (
            <div key={idx} className="text-[10px] text-green-300">{line}</div>
          ))}
        </div>
      )}
    </div>
    </div>
      </div>

      {/* ── Report Viewer Instances (one per open report, only active visible) ── */}
      {openReports.map((report, idx) => (
        <div key={report.id} style={{
          display: activeTabIdx === idx ? 'flex' : 'none',
          flex: 1, flexDirection: 'column', minHeight: 0, padding: '8px',
        }}>
          <InteractiveReportViewer
            columns={report.viewerData.columns}
            data={report.viewerData.data}
            formatRules={report.viewerData.formatRules}
            detailRowIndices={report.viewerData.detailRowIndices}
            summary={report.viewerData.summary}
            onDownloadExcel={() => handleDownloadExcel(idx)}
            onRefresh={() => handleRefreshReport(idx)}
            loading={loading}
            reportName={report.name}
            lastRunTime={report.lastRunTime}
            defaultHiddenColumnNames={report.defaultHiddenCols || []}
          />
        </div>
      ))}
    </div>
  );
};

export default ReportsModePanel;